import { useCallback, useEffect, useMemo, useState } from "react";
import {
  mockLogisticsTicketDetail,
  MOCK_TICKET_UUID,
} from "components/Logistics/Pickups/Details/mockLogisticsTicketDetail";
import { api } from "services/api.service";
import { LogisticsTicketQuoteRequest, LogisticsTicketVendor } from "types/logisticsTicket";
import { useAlert } from "./useAlert";

export type AvailableVendor = LogisticsTicketVendor & {
  phone?: string;
  vendorType: string;
  quoteRequest?: LogisticsTicketQuoteRequest;
};

type AvailableVendorsResponse = {
  vendors: AvailableVendor[];
};

export type AssignmentType = "immediate" | "request";

function assignmentSuccessMessage(assignmentType: AssignmentType): string {
  if (assignmentType === "immediate") {
    return "Vendor assigned successfully";
  }

  return "Quote requests sent successfully";
}

function assignmentErrorMessage(assignmentType: AssignmentType): string {
  if (assignmentType === "immediate") {
    return "Unable to assign vendor. Please try again.";
  }

  return "Unable to send quote requests. Please try again.";
}

type RequestQuotesOptions = {
  assignmentType?: AssignmentType;
  amountInCents?: number;
  scheduledAt?: string;
  confirmedTime?: string;
};

export default function useLogisticsTicketVendors(ticketUuid?: string) {
  const [vendors, setVendors] = useState<AvailableVendor[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const { alertError, alertSuccess } = useAlert();

  const fetchVendors = useCallback(() => {
    if (!ticketUuid) {
      setLoading(false);
      return;
    }

    setLoading(true);
    setError(null);

    api
      .get<AvailableVendorsResponse>(`/logistics_tickets/${ticketUuid}/available_vendors`)
      .then(({ data }) => {
        setVendors(data.vendors);
      })
      .catch((fetchError: Error) => {
        if (ticketUuid === MOCK_TICKET_UUID) {
          const sentAt = new Date().toISOString();
          setVendors(
            mockLogisticsTicketDetail.vendors.map((vendor, index) => ({
              id: index + 1,
              uuid: vendor.id,
              name: vendor.name,
              email: vendor.email,
              phone: vendor.phone,
              vendorType:
                vendor.vendorType ?? (vendor.name.toLowerCase().includes("revivn") ? "Internal Fleet" : "Freight"),
              quoteRequest:
                vendor.id === "vendor-1"
                  ? undefined
                  : {
                      id: index + 1,
                      uuid: `quote-request-${index}`,
                      sentAt,
                      quote:
                        vendor.id === "vendor-2"
                          ? {
                              id: index + 1,
                              uuid: `quote-${index}`,
                              amountInCents: 62800,
                              proposedDate: "2026-05-17",
                              submittedAt: sentAt,
                            }
                          : undefined,
                    },
            })),
          );
          setError(null);
          return;
        }

        setError(fetchError);
        setVendors([]);
      })
      .finally(() => {
        setLoading(false);
      });
  }, [ticketUuid]);

  const requestQuotes = useCallback(
    (vendorUuids: string[], options: RequestQuotesOptions = {}) => {
      if (!ticketUuid || vendorUuids.length === 0) {
        return Promise.resolve();
      }

      const assignmentType = options.assignmentType ?? "request";
      const assigning = assignmentType === "immediate";
      setSubmitting(true);

      return api
        .post(`/logistics_tickets/${ticketUuid}/quote_requests`, {
          vendorUuids,
          assignmentType,
          amountInCents: assigning && typeof options.amountInCents === "number" ? options.amountInCents : undefined,
          scheduledAt: assigning ? options.scheduledAt : undefined,
          confirmedTime: assigning ? options.confirmedTime : undefined,
        })
        .then(() => {
          alertSuccess(assignmentSuccessMessage(assignmentType));
          fetchVendors();
        })
        .catch((requestError: { response?: { data?: { errors?: string[] } } }) => {
          const reason = requestError.response?.data?.errors?.filter(Boolean).join(" ");
          alertError(reason || assignmentErrorMessage(assignmentType));
          return Promise.reject();
        })
        .finally(() => {
          setSubmitting(false);
        });
    },
    [alertError, alertSuccess, fetchVendors, ticketUuid],
  );

  useEffect(() => {
    fetchVendors();
  }, [fetchVendors]);

  return useMemo(
    () => ({
      vendors,
      loading,
      submitting,
      error,
      refetch: fetchVendors,
      requestQuotes,
    }),
    [vendors, loading, submitting, error, fetchVendors, requestQuotes],
  );
}
